import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import CIcon from '@coreui/icons-react'
import { cilChartLine } from '@coreui/icons'
import { useEntitlements } from '../../contexts/EntitlementsContext'
import { useIsModuleEnabled } from '../../hooks/useModuleAccess'
import useProfileModal from '../../hooks/useProfileModal'
import { getSectorLabel } from '../../utils/sectors'
import Avatar from '../../components/shared/Avatar'
import businessReferralService from '../../services/businessReferralService'
import './NetworkImpactSection.css'

// Section « Impact dans le réseau » de la fiche membre (onglet Réseau).
// AUTO-GATÉE (org_type='business' + module actif) + memberId valide. Consomme
// /members/:id/network-impact : générosité + niveau + activité coarse, SECTEURS
// où son réseau ouvre des portes (agrégat), et derniers apports (bénéficiaires
// découvrables, date coarse). Jamais de reçus, montants, clients ou litiges.
// Se masque silencieusement hors contexte / erreur.
//
// ⚠️ Hooks TOUS au-dessus des early returns (React #310 en prod).
const NetworkImpactSection = ({ memberId }) => {
  const { t } = useTranslation()
  const { orgType } = useEntitlements()
  const moduleEnabled = useIsModuleEnabled('business_referrals')
  const { openProfile, profileModal } = useProfileModal()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)

  const gated = orgType === 'business' && moduleEnabled && !!memberId

  useEffect(() => {
    let cancelled = false

    if (!gated) {
      setData(null)
      setLoading(false)
      return () => {
        cancelled = true
      }
    }

    setData(null)
    setLoading(true)

    businessReferralService
      .getMemberNetworkImpact(memberId)
      .then((res) => {
        if (!cancelled) setData(res || null)
      })
      .catch((error) => {
        if (!cancelled) {
          setData(null)
          // eslint-disable-next-line no-console
          console.error(
            '[NetworkImpactSection] échec chargement impact',
            error?.response?.status ?? error?.status ?? error?.message,
          )
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [gated, memberId])

  if (!gated || loading) return null

  const impact = data?.impact || {}
  // Défensif : les tableaux du contrat sont traités comme non fiables.
  const topSectorSlugs = Array.isArray(data?.topSectors) ? data.topSectors : []
  const rawContributions = Array.isArray(data?.recentContributions) ? data.recentContributions : []

  // Secteurs : on masque un slug inconnu (jamais de clé technique brute à l'écran).
  const sectors = topSectorSlugs
    .map((slug) => ({ slug, label: getSectorLabel(slug, t) }))
    .filter((s) => s.label)
  // Contributions : bénéficiaire sans id entier valide ignoré (jamais de profil
  // `undefined` au clic).
  const contributions = rawContributions.filter((c) =>
    Number.isInteger(Number(c?.recipient?.memberId)),
  )

  // `0` est une vraie valeur affichable (Nouveau contributeur 0/0 garde son badge).
  const hasDisplayable =
    Number.isFinite(impact.given) ||
    Number.isFinite(impact.confirmed) ||
    Boolean(impact.contributionLevel) ||
    Boolean(impact.activity) ||
    sectors.length > 0 ||
    contributions.length > 0
  if (!hasDisplayable) return null

  // Date coarse (granularité mois — jamais la date exacte).
  const coarseDate = (m) => {
    if (m == null) return ''
    if (m <= 0) return t('business:network_impact.this_month')
    if (m === 1) return t('business:network_impact.last_month')
    return t('business:network_impact.months_ago', { count: m })
  }

  return (
    <div className="member-section">
      <div className="member-section-title">
        <CIcon icon={cilChartLine} className="me-2" />
        {t('business:network_impact.title')}
      </div>

      <div className="ni-body">
        <div className="ni-stats">
          {Number.isFinite(impact.given) && (
            <div className="ni-stat">
              <span className="ni-stat__num">{impact.given}</span>
              <span className="ni-stat__label">{t('business:network_impact.given_label')}</span>
            </div>
          )}
          {Number.isFinite(impact.confirmed) && (
            <div className="ni-stat">
              <span className="ni-stat__num">{impact.confirmed}</span>
              <span className="ni-stat__label">{t('business:network_impact.confirmed_label')}</span>
            </div>
          )}
        </div>

        <div className="ni-tags">
          {impact.contributionLevel && (
            <span className={`ni-level ni-level--${impact.contributionLevel}`}>
              {t(`business:network_impact.level.${impact.contributionLevel}`)}
            </span>
          )}
          {impact.activity && (
            <span className="ni-activity">
              {t(`business:network_impact.activity.${impact.activity}`)}
            </span>
          )}
        </div>
      </div>

      {sectors.length > 0 && (
        <div className="ni-block">
          <div className="ni-block__title">{t('business:network_impact.sectors_title')}</div>
          <div className="ni-sectors">
            {sectors.map(({ slug, label }) => (
              <span key={slug} className="ni-sector">
                {label}
              </span>
            ))}
          </div>
        </div>
      )}

      {contributions.length > 0 && (
        <div className="ni-block">
          <div className="ni-block__title">{t('business:network_impact.recent_title')}</div>
          <ul className="ni-recent">
            {contributions.map((c, i) => {
              const rec = {
                id: Number(c.recipient.memberId),
                firstName: c.recipient.firstName,
                lastName: c.recipient.lastName,
              }
              const name = `${rec.firstName || ''} ${rec.lastName || ''}`.trim()
              const handler = openProfile(rec)
              return (
                <li key={i} className="ni-recent__item">
                  <span
                    className="ni-recent__who ni-recent__who--clickable"
                    onClick={handler}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        handler(e)
                      }
                    }}
                  >
                    <Avatar
                      user={rec}
                      firstName={rec.firstName}
                      lastName={rec.lastName}
                      size={26}
                      showCareerStatus={false}
                    />
                    <span className="ni-recent__text">
                      {t('business:network_impact.recommended_verb')} <strong>{name}</strong>
                    </span>
                  </span>
                  <span className="ni-recent__date">{coarseDate(c.monthsAgo)}</span>
                </li>
              )
            })}
          </ul>
        </div>
      )}

      {profileModal}
    </div>
  )
}

NetworkImpactSection.propTypes = {
  memberId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
}

export default NetworkImpactSection
